import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Typography, Alert, Space, Row, Col, Statistic, Progress,
  Button, DatePicker, Grid, Skeleton, Empty, theme,
} from 'antd';
import {
  CalendarOutlined, TeamOutlined, ReloadOutlined, RightOutlined,
  CheckCircleOutlined, ClockCircleOutlined, StopOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { fiscalAdminService } from '../../services/api';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

/**
 * Inicio del panel fiscal: cómo va el mes, de un vistazo y desde el celular.
 *
 * No sustituye a la agenda —ahí se trabaja—, responde la pregunta previa: cuánto
 * llevo, qué me falta y quién está atorado. Por eso cada indicador NAVEGA a la
 * agenda con su filtro puesto: verlo y no poder actuar obligaría a repetir el
 * filtro a mano, que es el gesto más caro en un teléfono.
 *
 * Todas las cifras salen de endpoints que ya existen; no hay backend nuevo.
 */

/** Tamaño de la consulta del desglose. Las bloqueadas son pocas por naturaleza. */
const TOPE_DESGLOSE = 200;

const MOTIVOS = {
  sin_efirma: { texto: 'sin e.firma cargada', icono: <SafetyCertificateOutlined /> },
  sin_consentimiento: { texto: 'sin autorización del contribuyente', icono: <StopOutlined /> },
};

const Hoy = () => {
  const { token } = theme.useToken();
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const esMovil = !screens.md;

  const [periodo, setPeriodo] = useState(null);
  const [datos, setDatos] = useState(null);
  const [bloqueo, setBloqueo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchTodo = useCallback(async () => {
    setLoading(true);
    setError('');

    const params = {};
    if (periodo) params.period = periodo.format('YYYY-MM');

    try {
      // El pulso viene del `resumen`, que es de TODA la cartera del periodo y no
      // depende de la página. Pedimos una página mínima: aquí no se listan filas.
      const agenda = await fiscalAdminService.getAgenda({ ...params, per_page: 1 });
      setDatos(agenda);

      // El desglose por motivo no viene agregado del servidor, así que se agrupan
      // las bloqueadas. Si hubiera más de las que caben en una página, se dice: una
      // suma incompleta presentada como total sería peor que no desglosar.
      const totalBloqueadas = agenda?.resumen?.bloqueadas || 0;
      if (totalBloqueadas === 0) {
        setBloqueo({ porMotivo: {}, completo: true });
      } else {
        try {
          const det = await fiscalAdminService.getAgenda({
            ...params,
            estado: 'bloqueada',
            per_page: TOPE_DESGLOSE,
          });
          const porMotivo = {};
          (det?.empresas || []).forEach((e) => {
            const clave = e.motivo_bloqueo || 'otro';
            porMotivo[clave] = (porMotivo[clave] || 0) + 1;
          });
          setBloqueo({ porMotivo, completo: totalBloqueadas <= TOPE_DESGLOSE });
        } catch (e) {
          // El desglose es accesorio: si falla, el total sigue siendo válido.
          setBloqueo(null);
        }
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Error al cargar el resumen del mes');
      console.error('Error fetching resumen fiscal:', err);
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => {
    fetchTodo();
  }, [fetchTodo]);

  const resumen = datos?.resumen || { total: 0, declaradas: 0, pendientes: 0, bloqueadas: 0 };
  const avance = resumen.total > 0 ? Math.round((resumen.declaradas / resumen.total) * 100) : 0;

  /**
   * Lleva a la agenda con el filtro puesto. Viaja por el estado de navegación y no
   * por la URL: la agenda gobierna sus filtros en estado local, y meterlos en la
   * URL sería rediseñar su sincronización entera. El precio es que recargar la
   * agenda la deja sin filtro, aceptable para un gesto de navegación.
   */
  const irAAgenda = (estado) => {
    navigate('/fiscal/agenda', {
      state: {
        estado: estado || null,
        periodo: periodo ? periodo.format('YYYY-MM') : datos?.periodo || null,
      },
    });
  };

  const Indicador = ({ titulo, valor, color, icono, estado, ayuda }) => (
    <Card
      size="small"
      hoverable
      onClick={() => irAAgenda(estado)}
      style={{ height: '100%' }}
      styles={{ body: { padding: esMovil ? 12 : 16 } }}
    >
      <Statistic
        title={
          <Space size={4}>
            {icono}
            <span>{titulo}</span>
          </Space>
        }
        value={valor}
        valueStyle={{ color, fontSize: esMovil ? 24 : 30 }}
      />
      <Text type="secondary" style={{ fontSize: 12 }}>
        {ayuda} <RightOutlined style={{ fontSize: 10 }} />
      </Text>
    </Card>
  );

  if (loading && !datos) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card styles={{ body: { padding: esMovil ? 16 : 24 } }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>
              Cómo va el mes
            </Title>
            <Text type="secondary">
              Periodo <Text strong>{datos?.periodo || '…'}</Text>. Es el trabajo que te
              toca presentar.
            </Text>
          </div>

          {error && <Alert type="error" message={error} showIcon />}

          {/* Periodo y recarga: a ancho completo en móvil para que quepan el dedo y
              el texto; en escritorio, en línea. */}
          <Space
            direction={esMovil ? 'vertical' : 'horizontal'}
            style={{ width: '100%' }}
            size="small"
          >
            <DatePicker
              picker="month"
              placeholder="Periodo"
              value={periodo}
              onChange={setPeriodo}
              allowClear
              style={{ width: esMovil ? '100%' : 200 }}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchTodo}
              loading={loading}
              block={esMovil}
            >
              Actualizar
            </Button>
          </Space>

          {resumen.total === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No tienes contribuyentes en tu cartera para este periodo. Un Admin debe asignarte al menos uno."
            />
          ) : (
            <>
              <div>
                <Space
                  style={{ width: '100%', justifyContent: 'space-between' }}
                  align="baseline"
                >
                  <Text strong>
                    {resumen.declaradas} de {resumen.total} declaradas
                  </Text>
                  <Text type="secondary">{avance}%</Text>
                </Space>
                <Progress
                  percent={avance}
                  showInfo={false}
                  strokeColor={avance === 100 ? token.colorSuccess : token.colorPrimary}
                />
              </div>

              <Row gutter={[12, 12]}>
                <Col xs={12} md={8}>
                  <Indicador
                    titulo="Pendientes"
                    valor={resumen.pendientes}
                    color={resumen.pendientes > 0 ? token.colorWarning : undefined}
                    icono={<ClockCircleOutlined />}
                    estado="pendiente"
                    ayuda="Trabajar ahora"
                  />
                </Col>
                <Col xs={12} md={8}>
                  <Indicador
                    titulo="Bloqueadas"
                    valor={resumen.bloqueadas}
                    color={resumen.bloqueadas > 0 ? token.colorError : undefined}
                    icono={<StopOutlined />}
                    estado="bloqueada"
                    ayuda="No dependen de ti"
                  />
                </Col>
                <Col xs={24} md={8}>
                  <Indicador
                    titulo="Declaradas"
                    valor={resumen.declaradas}
                    color={token.colorSuccess}
                    icono={<CheckCircleOutlined />}
                    estado="declarada"
                    ayuda="Ya presentadas"
                  />
                </Col>
              </Row>
            </>
          )}
        </Space>
      </Card>

      {/* El desglose separa lo que exige una llamada telefónica de lo que exige
          cargar un archivo: son gestiones distintas y ninguna se resuelve
          trabajando más. */}
      {resumen.bloqueadas > 0 && (
        <Card
          size="small"
          title={`Bloqueadas: ${resumen.bloqueadas}`}
          styles={{ body: { padding: esMovil ? 12 : 16 } }}
        >
          {bloqueo === null ? (
            <Text type="secondary">
              No se pudo cargar el desglose por motivo. El total sigue siendo válido.
            </Text>
          ) : (
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {Object.entries(bloqueo.porMotivo).map(([clave, cuantas]) => {
                const motivo = MOTIVOS[clave];
                return (
                  <Space key={clave} size={8}>
                    {motivo?.icono || <StopOutlined />}
                    <Text>
                      <Text strong>{cuantas}</Text>{' '}
                      {motivo?.texto || 'por otro motivo'}
                    </Text>
                  </Space>
                );
              })}
              {!bloqueo.completo && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Desglose de las primeras {TOPE_DESGLOSE}. Ábrelas en la agenda para
                  verlas todas.
                </Text>
              )}
              <Button type="link" style={{ padding: 0 }} onClick={() => irAAgenda('bloqueada')}>
                Ver quiénes son <RightOutlined />
              </Button>
            </Space>
          )}
        </Card>
      )}

      <Row gutter={[12, 12]}>
        <Col xs={24} md={12}>
          <Button
            block
            size="large"
            icon={<CalendarOutlined />}
            onClick={() => irAAgenda(null)}
          >
            Agenda del mes
          </Button>
        </Col>
        <Col xs={24} md={12}>
          <Button
            block
            size="large"
            icon={<TeamOutlined />}
            onClick={() => navigate('/fiscal/cartera')}
          >
            Mi cartera
          </Button>
        </Col>
      </Row>
    </Space>
  );
};

export default Hoy;
